import { _mapToObject, NumberEnum } from '@naturalcycles/js-lib'
import {
  BirthControlMethodBreakpointOption,
  ConceiveTimingBreakpointOption,
  EligibleAppleWatchBreakpointOption,
  Experiment,
  FeelingStressedBreakpointOption,
  HormonalBirthControlHowLongBreakpointOption,
  HormonalBirthControlUsageBreakpointOption,
  MedicalConditionsBreakpointOption,
  NightSleepBreakpointOption,
  SideEffectsBreakpointOption,
  SleepWithTrackerOption,
  WhichWearableBreakpointOption,
} from '@naturalcycles/shared'
import { allQuizSections } from '@src/app/constants/quiz.js'
import { QuizSignatureObject } from '@src/app/core/services/quiz/quiz.service.js'
import { QuizModifier } from '@src/app/core/util/quiz.util.js'
import { AnyPage, Page } from '@src/app/shared/typings/enum/pages.js'
import { Breakpoint, Sections } from '@src/app/shared/typings/enum/quiz.js'
import { SignupQuizFlow, WSQuizEngineConfig } from '@src/app/shared/typings/interfaces/quiz.js'
import {
  getAb312AllChaptersBreakpointWrapper,
  getAb312CycleChangesBreakpointAction,
  getAb312GoalBreakpointAction,
} from './ab312.js'

class Exp312Quiz implements QuizModifier<WSQuizEngineConfig> {
  public _shouldEnhance(obj: QuizSignatureObject): boolean {
    return obj.experiment[Experiment.QUIZ_CHAPTER] === 'test'
  }

  public enhanceEngineConfig(
    obj: QuizSignatureObject,
    config: WSQuizEngineConfig,
  ): WSQuizEngineConfig {
    if (!this._shouldEnhance(obj)) return config
    const isClearblue = obj.isClearblueFlow

    const goal = getAb312GoalBreakpointAction(config.breakpointActions[Breakpoint.goal])

    const defaultBehaviorBreakpoints: [Breakpoint, NumberEnum][] = [
      [Breakpoint.birthControlMethod, BirthControlMethodBreakpointOption],
      [Breakpoint.conceiveTiming, ConceiveTimingBreakpointOption],
      [Breakpoint.hormonalBirthControlUsage, HormonalBirthControlUsageBreakpointOption],
      [Breakpoint.whichWearable, WhichWearableBreakpointOption],
      [Breakpoint.eligibleAppleWatch, EligibleAppleWatchBreakpointOption],
      [Breakpoint.feelingStressed, FeelingStressedBreakpointOption],
      [Breakpoint.nightSleep, NightSleepBreakpointOption],
      [Breakpoint.sleepWearable, SleepWithTrackerOption],
      [Breakpoint.medicalConditions, MedicalConditionsBreakpointOption],
      [Breakpoint.hormonalBirthControlHowLong, HormonalBirthControlHowLongBreakpointOption],
      [Breakpoint.sideEffects, SideEffectsBreakpointOption],
    ]

    // Makes sure the chapters are there in every section, always
    const breakpointActions = _mapToObject(defaultBehaviorBreakpoints, ([breakpoint, option]) => [
      breakpoint,
      getAb312AllChaptersBreakpointWrapper(
        config.breakpointActions[breakpoint],
        option,
        isClearblue,
      ),
    ])
    config.breakpointActions = breakpointActions

    // These have some extra logic
    config.breakpointActions[Breakpoint.goal] = goal
    config.breakpointActions[Breakpoint.cycleChanges] = getAb312CycleChangesBreakpointAction()

    return config
  }

  public enhanceSections(obj: QuizSignatureObject, sections: Sections): Sections {
    if (!this._shouldEnhance(obj)) return sections
    return sections
  }

  public enhanceInitialFlow(obj: QuizSignatureObject, flow: SignupQuizFlow): SignupQuizFlow {
    if (!this._shouldEnhance(obj)) return flow

    const introSection = flow[allQuizSections.intro]

    if (!introSection) return flow
    if (introSection.includes(Page.introChapter)) return flow

    const namePageIndex = introSection.indexOf(Page.name)
    if (namePageIndex === -1) return flow

    introSection.splice(namePageIndex, 0, Page.introChapter)
    return flow
  }

  public enhanceEntry(obj: QuizSignatureObject, entry: AnyPage[] | null): AnyPage[] | null {
    if (!this._shouldEnhance(obj)) return entry
    return entry
  }

  public enhanceExit(obj: QuizSignatureObject, exit: AnyPage[] | null): AnyPage[] | null {
    if (!this._shouldEnhance(obj)) return exit
    return exit
  }
}

export const exp312Quiz = new Exp312Quiz()
